import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData, VerifyData } from '../../../apiService';
import LoadingOverlay from '../../common/LoadingOverlay';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { usePermissions } from '../../../usePermissions';
import {
  Add, Edit, Visibility, Delete, RequestQuote, FilterList, Refresh, Warning, CheckCircle, Cancel, ShoppingCart
} from '@mui/icons-material';
import {
  Button, IconButton, Tooltip, Chip, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box
} from '@mui/material';
import SaleOrderModal from '../SaleOrder/SaleOrderModal';
import QuotationModal from './QuotationModal';
import './QuotationIndex.css';

const QuotationIndex = () => {
  // Session validation
  useSessionValidation();

  const { companyId, branchId, token, userId } = useAuth();
  const { canCreateTransaction, canEditTransaction, canDeleteTransaction } = usePermissions();

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // Data states
  const [quotations, setQuotations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVerified, setFilterVerified] = useState('all');

  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getCurrentDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form states
  const [formData, setFormData] = useState({});
  const [quotationDetails, setQuotationDetails] = useState([]);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete confirmation modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');

  // Sales Order modal states
  const [salesOrderModalOpen, setSalesOrderModalOpen] = useState(false);
  const [salesOrderFormData, setSalesOrderFormData] = useState({});
  const [salesOrderDetails, setSalesOrderDetails] = useState([]);

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
      fetchAllData(fromDate, toDate);
    }
  }, [companyId, token, fromDate, toDate]);

  // Force re-render when quotations change
  const [renderKey, setRenderKey] = useState(0);

  useEffect(() => {
    console.log('Quotations state changed:', quotations);
    setRenderKey(prev => prev + 1);
  }, [quotations]);

  // Fetch functions
  const fetchQuotations = async (fromDateParam = '', toDateParam = '') => {
    try {
      let endpoint = `/${companyId}/${branchId}/`;

      // Add date range parameters if provided
      if (fromDateParam && toDateParam) {
        endpoint += `${fromDateParam}/${toDateParam}`;
      } else if (fromDateParam) {
        endpoint += `${fromDateParam}`;
      } else if (toDateParam) {
        endpoint += `/${toDateParam}`;
      }

      console.log('Fetching quotations from endpoint:', endpoint);
      const response = await fetchData('QuotationApi', endpoint, token);
      console.log('Raw API response:', response);

      // Handle different response structures
      let quotationData = [];
      if (response) {
        if (response.response_data) {
          quotationData = Array.isArray(response.response_data) ? response.response_data : [];
        } else if (Array.isArray(response)) {
          quotationData = response;
        } else if (response.data) {
          quotationData = Array.isArray(response.data) ? response.data : [];
        } else if (response.quotations) {
          quotationData = Array.isArray(response.quotations) ? response.quotations : [];
        }
      }

      console.log('Processed quotation data:', quotationData);
      console.log('Number of quotations:', quotationData.length);

      // Force state update with callback to ensure re-render
      setQuotations(prevQuotations => {
        console.log('Previous quotations:', prevQuotations);
        console.log('Setting new quotations:', quotationData);
        return [...quotationData]; // Create new array reference
      });
    } catch (error) {
      console.error('Error fetching quotations:', error);
      setQuotations([]);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await fetchData('ClientAndVendorApi', `/ClientList/${companyId}`, token);
      setCustomers(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await fetchData('ItemApi', `/${companyId}`, token);
      setItems(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    }
  };

  const fetchMaxBillNo = async () => {
    try {
      const response = await fetchData('QuotationApi', `/MaxBillno/${companyId}/${branchId || 1}`, token);
      console.log('MaxBillNo API Response:', response);
      const data = response.response_data || response || {};
      console.log('MaxBillNo Data:', data);

      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || '';
      const finYear = data.FinYear || data.finYear || '';

      // Generate QuotationNo based on available data
      let quotationNo = '';
      if (billPrefix && finYear) {
        quotationNo = `${billPrefix}/${billNumber}/${finYear}`;
      } else if (finYear) {
        quotationNo = `${billNumber}/${finYear}`;
      } else if (billPrefix) {
        quotationNo = `${billPrefix}/${billNumber}`;
      } else {
        quotationNo = `${billNumber}`;
      }

      return { billNumber, billPrefix, finYear, quotationNo };
    } catch (error) {
      console.error('Error fetching max bill number:', error);
      return { billNumber: 1, billPrefix: '', finYear: '', quotationNo: '1' };
    }
  };

  const fetchMaxSalesOrderBillNo = async () => {
    try {
      // Try to fetch from SalesOrderApi first, fallback to QuotationApi pattern
      const response = await fetchData('SalesOrderApi', `/MaxBillno/${companyId}/${branchId || 1}`, token);
      console.log('MaxSalesOrderBillNo API Response:', response);
      const data = response.response_data || response || {};
      console.log('MaxSalesOrderBillNo Data:', data);

      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || 'SO';
      const finYear = data.FinYear || data.finYear || '';

      // Generate SalesOrderNo based on available data
      let salesOrderNo = '';
      if (billPrefix && finYear) {
        salesOrderNo = `${billPrefix}/${billNumber}/${finYear}`;
      } else if (finYear) {
        salesOrderNo = `${billNumber}/${finYear}`;
      } else if (billPrefix) {
        salesOrderNo = `${billPrefix}/${billNumber}`;
      } else {
        salesOrderNo = `${billNumber}`;
      }

      return { billNumber, billPrefix, finYear, salesOrderNo };
    } catch (error) {
      console.error('Error fetching max sales order bill number:', error);
      // Fallback to default values
      return { billNumber: 1, billPrefix: 'SO', finYear: '', salesOrderNo: 'SO/1' };
    }
  };

  // Freight + Packing GST state
  const [freightPackingGST, setFreightPackingGST] = useState({ freight: null, packing: null });

  // Freight + Packing GST
  const fetchFreightAndPackingGST = async () => {
    try {
      const response = await fetchData('QuotationApi', `/AccountHeadFreightAndPacking/${companyId}/${branchId || 1}`, token);
      console.log('AccountHeadFreightAndPacking API Response:', response);
      const data = response.response_data || response || [];
      console.log('AccountHeadFreightAndPacking Data:', data);

      // Find Freight and Packing entries from the response
      let freightData = null;
      let packingData = null;

      if (Array.isArray(data)) {
        freightData = data.find(item => item.Name?.toLowerCase().includes('freight') || item.AccountHeadName?.toLowerCase().includes('freight'));
        packingData = data.find(item => item.Name?.toLowerCase().includes('packing') || item.AccountHeadName?.toLowerCase().includes('packing'));
      } else if (data) {
        // If it's an object with Freight and Packing properties
        freightData = data.Freight || data.FreightCharge || null;
        packingData = data.Packing || data.PackingCharge || null;
      }

      setFreightPackingGST({
        freight: freightData,
        packing: packingData
      });

      return { freight: freightData, packing: packingData };
    } catch (error) {
      console.error('Error fetching freight and packing GST:', error);
      return { freight: null, packing: null };
    }
  };

  const fetchAllData = async (fromDateParam = '', toDateParam = '') => {
    setLoading(true);
    setLoadingMessage('Fetching quotations...');
    await Promise.allSettled([
      fetchQuotations(fromDateParam, toDateParam),
      fetchCustomers(),
      fetchItems(),
      fetchFreightAndPackingGST()
    ]);
    setLoading(false);
  };

  const fetchSingleQuotation = async (id) => {
    try {
      const response = await fetchData('QuotationApi', `/${id}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching quotation details:', error);
      return null;
    }
  };

  // Modal handlers
  const handleOpenModal = async (action, itemOrId = null) => {
    setModalAction(action);
    const itemId = typeof itemOrId === 'object' ? itemOrId?.Id : itemOrId;

    if ((action === 'view' || action === 'edit') && itemId) {
      setModalLoading(true);

      const freshData = await fetchSingleQuotation(itemId);

      if (freshData) {
        const quotationData = freshData.Quotation || freshData;
        const details = freshData.QuotationDetails || freshData.Details || [];

        setSelectedItem(quotationData);
        setFormData({
          Id: quotationData.Id,
          CompanyId: quotationData.CompanyId || companyId,
          BranchId: quotationData.BranchId || 0,
          MasterId: quotationData.MasterId,
          InvoiceTypeId: quotationData.InvoiceTypeId || 0,
          QuotationNo: quotationData.QuotationNo,
          QuotationNumber: quotationData.QuotationNumber || 0,
          QuotationPrefix: quotationData.QuotationPrefix || '',
          QuotationDate: quotationData.QuotationDate?.split('T')[0] || new Date().toISOString().split('T')[0],
          FinYear: quotationData.FinYear || '',
          Instruction1: quotationData.Instruction1 || '',
          Instruction2: quotationData.Instruction2 || '',
          Remarks: quotationData.Remarks || '',
          Amount: quotationData.Amount || 0,
          DiscountAmount: quotationData.DiscountAmount || 0,
          TaxableAmount: quotationData.TaxableAmount || 0,
          CGSTAmount: quotationData.CGSTAmount || 0,
          SGSTAmount: quotationData.SGSTAmount || 0,
          IGSTAmount: quotationData.IGSTAmount || 0,
          FreightAmount: quotationData.FreightAmount || 0,
          FreightGSTRate: quotationData.FreightGSTRate || 0,
          FreightGSTAmount: quotationData.FreightGSTAmount || 0,
          PackingAmount: quotationData.PackingAmount || 0,
          PackingGSTRate: quotationData.PackingGSTRate || 0,
          PackingGSTAmount: quotationData.PackingGSTAmount || 0,
          RoundOff: quotationData.RoundOff || 0,
          TotalAmount: quotationData.TotalAmount || 0,
          IsConverted: quotationData.IsConverted || false,
          IsVerified: quotationData.IsVerified || false,
          IsActive: quotationData.IsActive ?? true
        });
        setQuotationDetails(details.map(d => ({
          Id: d.Id || 0,
          ItemId: d.ItemId,
          HSNCode: d.HSNCode || '',
          GSTRate: d.GSTRate || 0,
          Quantity: d.Quantity || 0,
          Rate: d.Rate || 0,
          Amount: d.Amount || 0,
          DiscountPercentage: d.DiscountPercentage || 0,
          DiscountAmount: d.DiscountAmount || 0,
          TaxableAmount: d.TaxableAmount || 0,
          CGSTAmount: d.CGSTAmount || 0,
          SGSTAmount: d.SGSTAmount || 0,
          IGSTAmount: d.IGSTAmount || 0,
          TotalAmount: d.TotalAmount || 0,
          IsActive: d.IsActive ?? true
        })));
      } else {
        const item = typeof itemOrId === 'object' ? itemOrId : null;
        setSelectedItem(item);
        setFormData(item || {});
        setQuotationDetails([]);
      }

      setModalLoading(false);
    } else {
      // Create mode - fetch max bill number and freight/packing GST
      setModalLoading(true);

      const maxBillData = await fetchMaxBillNo();
      const gstData = await fetchFreightAndPackingGST();

      // Get GST rates from API data
      const freightGSTRate = gstData.freight?.GSTRate || gstData.freight?.GstRate || 0;
      const packingGSTRate = gstData.packing?.GSTRate || gstData.packing?.GstRate || 0;

      setSelectedItem(null);
      setFormData({
        CompanyId: companyId,
        BranchId: branchId || 1,
        MasterId: '',
        InvoiceTypeId: 0,
        QuotationNumber: maxBillData.billNumber,
        QuotationNo: maxBillData.quotationNo,
        QuotationPrefix: maxBillData.billPrefix,
        QuotationDate: new Date().toISOString().split('T')[0],
        FinYear: maxBillData.finYear,
        Instruction1: '',
        Instruction2: '',
        Remarks: '',
        Amount: 0,
        DiscountAmount: 0,
        TaxableAmount: 0,
        CGSTAmount: 0,
        SGSTAmount: 0,
        IGSTAmount: 0,
        FreightAmount: 0,
        FreightGSTRate: freightGSTRate,
        FreightGSTAmount: 0,
        PackingAmount: 0,
        PackingGSTRate: packingGSTRate,
        PackingGSTAmount: 0,
        RoundOff: 0,
        TotalAmount: 0,
        IsConverted: false,
        IsVerified: false,
        IsActive: true
      });
      setQuotationDetails([]);
      setModalLoading(false);
    }

    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalAction('');
    setSelectedItem(null);
    setFormData({});
    setQuotationDetails([]);
  };

  // Save handler
  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      const actionText = modalAction === 'create' ? 'Creating' : 'Updating';
      setLoadingMessage(`${actionText} quotation...`);

      const dataToSend = {
        Quotation: {
          Id: modalAction === 'edit' ? (formData.Id || 0) : 0,
          CompanyId: companyId,
          BranchId: formData.BranchId || 0,
          MasterId: parseInt(formData.MasterId) || 0,
          InvoiceTypeId: formData.InvoiceTypeId || 0,
          QuotationNo: formData.QuotationNo || '',
          QuotationNumber: formData.QuotationNumber || 0,
          QuotationPrefix: formData.QuotationPrefix || '',
          QuotationDate: formData.QuotationDate || new Date().toISOString().split('T')[0],
          FinYear: formData.FinYear || '',
          Instruction1: formData.Instruction1 || '',
          Instruction2: formData.Instruction2 || '',
          Remarks: formData.Remarks || '',
          Amount: parseFloat(formData.Amount) || 0,
          DiscountAmount: parseFloat(formData.DiscountAmount) || 0,
          TaxableAmount: parseFloat(formData.TaxableAmount) || 0,
          CGSTAmount: parseFloat(formData.CGSTAmount) || 0,
          SGSTAmount: parseFloat(formData.SGSTAmount) || 0,
          IGSTAmount: parseFloat(formData.IGSTAmount) || 0,
          FreightAmount: parseFloat(formData.FreightAmount) || 0,
          FreightGSTRate: parseFloat(formData.FreightGSTRate) || 0,
          FreightGSTAmount: parseFloat(formData.FreightGSTAmount) || 0,
          PackingAmount: parseFloat(formData.PackingAmount) || 0,
          PackingGSTRate: parseFloat(formData.PackingGSTRate) || 0,
          PackingGSTAmount: parseFloat(formData.PackingGSTAmount) || 0,
          RoundOff: parseFloat(formData.RoundOff) || 0,
          TotalAmount: parseFloat(formData.TotalAmount) || 0,
          IsConverted: formData.IsConverted || false,
          IsVerified: formData.IsVerified || false,
          IsActive: formData.IsActive ?? true,
          CreatedBy: modalAction === 'create' ? userId : formData.CreatedBy,
          ModifiedBy: modalAction === 'edit' ? userId : null
        },
        QuotationDetails: quotationDetails.map(detail => ({
          Id: modalAction === 'edit' ? (detail.Id || 0) : 0,
          CompanyId: companyId,
          QuotationId: modalAction === 'edit' ? formData.Id : 0,
          ItemId: parseInt(detail.ItemId) || 0,
          HSNCode: detail.HSNCode || '',
          GSTRate: parseFloat(detail.GSTRate) || 0,
          Quantity: parseFloat(detail.Quantity) || 0,
          Rate: parseFloat(detail.Rate) || 0,
          Amount: parseFloat(detail.Amount) || 0,
          DiscountPercentage: parseFloat(detail.DiscountPercentage) || 0,
          DiscountAmount: parseFloat(detail.DiscountAmount) || 0,
          TaxableAmount: parseFloat(detail.TaxableAmount) || 0,
          CGSTAmount: parseFloat(detail.CGSTAmount) || 0,
          SGSTAmount: parseFloat(detail.SGSTAmount) || 0,
          IGSTAmount: parseFloat(detail.IGSTAmount) || 0,
          TotalAmount: parseFloat(detail.TotalAmount) || 0,
          IsActive: detail.IsActive ?? true,
          CreatedBy: modalAction === 'create' ? userId : detail.CreatedBy,
          ModifiedBy: modalAction === 'edit' ? userId : null
        }))
      };

      if (modalAction === 'create') {
        await postData('QuotationApi', '', dataToSend, token);
        setSuccess('Quotation created successfully!');
      } else if (modalAction === 'edit') {
        if (!formData.Id) throw new Error('Quotation ID is missing');
        await updateData('QuotationApi', formData.Id, dataToSend, token);
        setSuccess('Quotation updated successfully!');
      }

      handleCloseModal();
      await fetchQuotations(fromDate, toDate);
      setLoading(false);
    } catch (error) {
      console.error('Error saving:', error);
      setError(`Failed to ${modalAction} quotation. Please try again.`);
      setLoading(false);
    }
  };

  // Delete handlers
  const handleOpenDeleteModal = (id, quotationNo) => {
    setDeleteItemId(id);
    setDeleteItemNo(quotationNo);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteItemId(null);
    setDeleteItemNo('');
  };

  const handleDelete = async () => {
    if (!deleteItemId) return;

    setLoading(true);
    handleCloseDeleteModal();
    try {
      await deleteData('QuotationApi', deleteItemId, token);
      setSuccess('Quotation deleted successfully!');
      await fetchQuotations(fromDate, toDate);
    } catch (error) {
      console.error('Error deleting:', error);
      setError('Failed to delete quotation.');
    }
    setLoading(false);
  };

  // Conversion handlers
  const handleConvertToSalesOrder = async (quotationId, quotationNo) => {
    if (!quotationId) return;

    setLoading(true);
    setLoadingMessage('Preparing sales order from quotation...');

    try {
      // Fetch the quotation details
      const quotationData = await fetchSingleQuotation(quotationId);

      if (quotationData) {
        const quotation = quotationData.Quotation || quotationData;
        const details = quotationData.QuotationDetails || quotationData.Details || [];

        // Get max sales order bill number
        const maxBillData = await fetchMaxSalesOrderBillNo();

        // Convert quotation data to sales order format
        const salesOrderData = {
          CompanyId: companyId,
          BranchId: branchId || 1,
          MasterId: quotation.MasterId,
          InvoiceTypeId: quotation.InvoiceTypeId || 0,
          SalesOrderNo: maxBillData.salesOrderNo,
          SalesOrderNumber: maxBillData.billNumber,
          SalesOrderPrefix: maxBillData.billPrefix,
          SalesOrderDate: new Date().toISOString().split('T')[0],
          FinYear: maxBillData.finYear,
          Instruction1: quotation.Instruction1 || '',
          Instruction2: quotation.Instruction2 || '',
          Remarks: quotation.Remarks || '',
          Amount: quotation.Amount || 0,
          DiscountAmount: quotation.DiscountAmount || 0,
          TaxableAmount: quotation.TaxableAmount || 0,
          CGSTAmount: quotation.CGSTAmount || 0,
          SGSTAmount: quotation.SGSTAmount || 0,
          IGSTAmount: quotation.IGSTAmount || 0,
          FreightAmount: quotation.FreightAmount || 0,
          FreightGSTRate: quotation.FreightGSTRate || 0,
          FreightGSTAmount: quotation.FreightGSTAmount || 0,
          PackingAmount: quotation.PackingAmount || 0,
          PackingGSTRate: quotation.PackingGSTRate || 0,
          PackingGSTAmount: quotation.PackingGSTAmount || 0,
          RoundOff: quotation.RoundOff || 0,
          TotalAmount: quotation.TotalAmount || 0,
          IsConverted: false,
          IsVerified: false,
          IsActive: true,
          QuotationId: quotationId // Reference to original quotation
        };

        // Convert quotation details to sales order details
        const salesOrderDetailsData = details.map(d => ({
          Id: 0, // New record
          ItemId: d.ItemId,
          HSNCode: d.HSNCode || '',
          GSTRate: d.GSTRate || 0,
          Quantity: d.Quantity || 0,
          Rate: d.Rate || 0,
          Amount: d.Amount || 0,
          DiscountPercentage: d.DiscountPercentage || 0,
          DiscountAmount: d.DiscountAmount || 0,
          TaxableAmount: d.TaxableAmount || 0,
          CGSTAmount: d.CGSTAmount || 0,
          SGSTAmount: d.SGSTAmount || 0,
          IGSTAmount: d.IGSTAmount || 0,
          TotalAmount: d.TotalAmount || 0,
          IsActive: true
        }));

        // Set the sales order data and open modal
        setSalesOrderFormData(salesOrderData);
        setSalesOrderDetails(salesOrderDetailsData);
        setSalesOrderModalOpen(true);

        setSuccess(`Quotation ${quotationNo} data loaded for sales order creation.`);
      } else {
        throw new Error('Failed to fetch quotation details');
      }
    } catch (error) {
      console.error('Error preparing sales order:', error);
      setError(`Failed to prepare sales order from quotation ${quotationNo}. ${error.message || 'Please try again.'}`);
    }

    setLoading(false);
  };

  // Verify handler
  const handleVerifyQuotation = async (quotationId, quotationNo) => {
    if (!quotationId) return;

    setLoading(true);
    setLoadingMessage('Verifying quotation...');

    try {
      const response = await VerifyData('QuotationApi', quotationId, token);

      if (response && response.success !== false) {
        setSuccess(`Quotation ${quotationNo} verified successfully!`);
        // Refresh the quotations list to show updated verification status
        await fetchQuotations(fromDate, toDate);
        // Close the modal after successful verification
        handleCloseModal();
      } else {
        throw new Error(response?.message || 'Verification failed');
      }
    } catch (error) {
      console.error('Error verifying quotation:', error);
      setError(`Failed to verify quotation ${quotationNo}. ${error.message || 'Please try again.'}`);
    }

    setLoading(false);
  };

  // Sales Order modal handlers
  const handleCloseSalesOrderModal = () => {
    setSalesOrderModalOpen(false);
    setSalesOrderFormData({});
    setSalesOrderDetails([]);
  };

  const handleSaveSalesOrder = async () => {
    setLoading(true);
    setError('');

    try {
      setLoadingMessage('Creating sales order...');

      const dataToSend = {
        SaleOrder: {
          Id: 0, // New record
          CompanyId: companyId,
          BranchId: salesOrderFormData.BranchId || 0,
          MasterId: parseInt(salesOrderFormData.MasterId) || 0,
          InvoiceTypeId: salesOrderFormData.InvoiceTypeId || 0,
          SaleOrderNo: salesOrderFormData.SalesOrderNo || '',
          SaleOrderNumber: salesOrderFormData.SalesOrderNumber || 0,
          SaleOrderPrefix: salesOrderFormData.SalesOrderPrefix || '',
          SaleOrderDate: salesOrderFormData.SalesOrderDate || new Date().toISOString().split('T')[0],
          FinYear: salesOrderFormData.FinYear || '',
          Instruction1: salesOrderFormData.Instruction1 || '',
          Instruction2: salesOrderFormData.Instruction2 || '',
          Remarks: salesOrderFormData.Remarks || '',
          Amount: parseFloat(salesOrderFormData.Amount) || 0,
          DiscountAmount: parseFloat(salesOrderFormData.DiscountAmount) || 0,
          TaxableAmount: parseFloat(salesOrderFormData.TaxableAmount) || 0,
          CGSTAmount: parseFloat(salesOrderFormData.CGSTAmount) || 0,
          SGSTAmount: parseFloat(salesOrderFormData.SGSTAmount) || 0,
          IGSTAmount: parseFloat(salesOrderFormData.IGSTAmount) || 0,
          FreightAmount: parseFloat(salesOrderFormData.FreightAmount) || 0,
          FreightGSTRate: parseFloat(salesOrderFormData.FreightGSTRate) || 0,
          FreightGSTAmount: parseFloat(salesOrderFormData.FreightGSTAmount) || 0,
          PackingAmount: parseFloat(salesOrderFormData.PackingAmount) || 0,
          PackingGSTRate: parseFloat(salesOrderFormData.PackingGSTRate) || 0,
          PackingGSTAmount: parseFloat(salesOrderFormData.PackingGSTAmount) || 0,
          RoundOff: parseFloat(salesOrderFormData.RoundOff) || 0,
          TotalAmount: parseFloat(salesOrderFormData.TotalAmount) || 0,
          IsConverted: false,
          IsVerified: false,
          IsActive: true,
          CreatedBy: userId,
          QuotationId: salesOrderFormData.QuotationId || null
        },
        SaleOrderDetails: salesOrderDetails.map(detail => ({
          Id: 0, // New record
          CompanyId: companyId,
          SaleOrderId: 0, // Will be set by backend
          ItemId: parseInt(detail.ItemId) || 0,
          HSNCode: detail.HSNCode || '',
          GSTRate: parseFloat(detail.GSTRate) || 0,
          Quantity: parseFloat(detail.Quantity) || 0,
          Rate: parseFloat(detail.Rate) || 0,
          Amount: parseFloat(detail.Amount) || 0,
          DiscountPercentage: parseFloat(detail.DiscountPercentage) || 0,
          DiscountAmount: parseFloat(detail.DiscountAmount) || 0,
          TaxableAmount: parseFloat(detail.TaxableAmount) || 0,
          CGSTAmount: parseFloat(detail.CGSTAmount) || 0,
          SGSTAmount: parseFloat(detail.SGSTAmount) || 0,
          IGSTAmount: parseFloat(detail.IGSTAmount) || 0,
          TotalAmount: parseFloat(detail.TotalAmount) || 0,
          IsActive: true,
          CreatedBy: userId
        }))
      };

      // Try to save to SalesOrderApi
      try {
        await postData('SaleOrderApi', '', dataToSend, token);
        setSuccess('Sales order created successfully from quotation!');

        // Mark the original quotation as converted if we have the QuotationId
        if (salesOrderFormData.QuotationId) {
          try {
            // Mark quotation as converted using a PUT request
            await updateData('QuotationApi', `${salesOrderFormData.QuotationId}/Convert`, {}, token);
            await fetchQuotations(fromDate, toDate); // Refresh quotations list
          } catch (conversionError) {
            console.warn('Failed to mark quotation as converted:', conversionError);
            // Don't fail the whole operation if this fails
          }
        }

        handleCloseSalesOrderModal();
      } catch (apiError) {
        // If SalesOrderApi doesn't exist, show a helpful message
        if (apiError.response?.status === 404) {
          setError('Sales Order API is not yet implemented. Please contact your system administrator.');
        } else {
          throw apiError;
        }
      }

      setLoading(false);
    } catch (error) {
      console.error('Error saving sales order:', error);
      setError('Failed to create sales order. Please try again.');
      setLoading(false);
    }
  };

  // Filter and pagination
  const getFilteredData = (data) => {
    console.log('getFilteredData input:', data);

    if (!Array.isArray(data)) {
      console.warn('getFilteredData received non-array data:', typeof data, data);
      return [];
    }

    let filtered = [...data]; // Create a copy to avoid mutations

    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.QuotationNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.CustomerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customers.find(c => c.MasterId === item.MasterId)?.Name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(item =>
        filterStatus === 'active' ? item.IsActive : !item.IsActive
      );
    }

    if (filterVerified !== 'all') {
      filtered = filtered.filter(item =>
        filterVerified === 'verified' ? item.IsVerified : !item.IsVerified
      );
    }

    console.log('getFilteredData output:', filtered);
    return filtered;
  };

  const getPaginatedData = (data) => {
    console.log('getPaginatedData input:', data);

    const filtered = getFilteredData(data) || [];
    console.log('getPaginatedData filtered:', filtered);

    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    const paginatedResult = {
      data: Array.isArray(filtered) ? filtered.slice(startIndex, endIndex) : [],
      totalCount: Array.isArray(filtered) ? filtered.length : 0,
      totalPages: Math.ceil((Array.isArray(filtered) ? filtered.length : 0) / rowsPerPage),
      startIndex
    };

    console.log('getPaginatedData result:', paginatedResult);
    return paginatedResult;
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleRowsPerPageChange = (e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); };
  const handleRefresh = () => fetchAllData(fromDate, toDate);
  const handleCloseSnackbar = () => { setError(''); setSuccess(''); };

  // Date change handlers
  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
    setCurrentPage(1);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value);
    setCurrentPage(1);
  };

  // Format helpers
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const { data: paginatedData, totalCount, totalPages, startIndex } = getPaginatedData(quotations);

  return (
    <div className="quotation-dashboard">
      <div className="dashboard-header">
        <h5 className="plain-header">
          Quotation Management
        </h5>
      </div>

      <div className="dashboard-content">
        <div className="data-table-container">
          <div className="table-header">
            <div className="search-filter-section">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search by Quotation No, Customer..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
              </div>

              {/* Date Range Filter */}
              <div className="date-filter-section">
                <input
                  type="date"
                  value={fromDate}
                  onChange={handleFromDateChange}
                  className="date-input"
                  title="From Date"
                />
                <input
                  type="date"
                  value={toDate}
                  onChange={handleToDateChange}
                  className="date-input"
                  title="To Date"
                />
              </div>

              <div className="filter-section">
                <FilterList className="filter-icon" />
                <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}>
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="filter-section">
                <select value={filterVerified} onChange={(e) => { setFilterVerified(e.target.value); setCurrentPage(1); }}>
                  <option value="all">All</option>
                  <option value="verified">Verified</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              {/* Desktop buttons */}
              <Button variant="outlined" onClick={handleRefresh} disabled={loading} className="refresh-button desktop-only" startIcon={<Refresh />}>
                Refresh
              </Button>
              {canCreateTransaction && (
                <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenModal('create')} className="add-button desktop-only">
                  Add Quotation
                </Button>
              )}
              {/* Mobile icon buttons */}
              <Tooltip title="Refresh">
                <IconButton onClick={handleRefresh} disabled={loading} className="refresh-icon-btn mobile-only">
                  <Refresh />
                </IconButton>
              </Tooltip>
              {canCreateTransaction && (
                <Tooltip title="Add Quotation">
                  <IconButton onClick={() => handleOpenModal('create')} className="add-icon-btn mobile-only">
                    <Add />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </div>

          <div className="table-wrapper">
            <table className="data-table" key={`quotation-table-${renderKey}`}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'center' }}>Sl No.</th>
                  <th>Quotation No.</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Total Amount</th>
                  <th>Verified</th>
                  <th>Converted</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(paginatedData) && paginatedData.length > 0 ? (
                  paginatedData.map((item, index) => (
                    <tr key={item.Id || index}>
                      <td style={{ textAlign: 'center' }}>
                        <span onClick={() => handleOpenModal('edit', item)} style={{ color: '#1e40af', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline' }}>
                          {startIndex + index + 1}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <RequestQuote sx={{ fontSize: 18, color: '#1e40af' }} />
                          {item.QuotationPrefix || '-'}/{item.QuotationNumber || '-'}/{item.FinYear || '-'}
                        </div>
                      </td>
                      <td>{formatDate(item.QuotationDate)}</td>
                      <td>{item.CustomerName || customers.find(c => c.MasterId === item.MasterId)?.Name || '-'}</td>
                      <td style={{ fontWeight: 700 }}>{formatCurrency(item.TotalAmount)}</td>
                      <td>
                        {item.IsVerified ? (
                          <Chip icon={<CheckCircle sx={{ fontSize: 14 }} />} label="Verified" color="success" size="small" sx={{ fontSize: '0.65rem', height: '22px' }} />
                        ) : (
                          <Chip icon={<Cancel sx={{ fontSize: 14 }} />} label="Pending" color="warning" size="small" sx={{ fontSize: '0.65rem', height: '22px' }} />
                        )}
                      </td>
                      <td>
                        {item.IsConverted ? (
                          <Chip label="Converted" color="info" size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                        ) : (
                          <Chip label="Open" color="default" size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                        )}
                      </td>
                      <td>
                        <Chip
                          label={item.IsActive ? 'Active' : 'Inactive'}
                          color={item.IsActive ? 'success' : 'default'}
                          size="small"
                          sx={{ fontSize: '0.65rem', height: '20px' }}
                        />
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="action-buttons">
                          <Tooltip title="View">
                            <IconButton size="small" onClick={() => handleOpenModal('view', item.Id)} className="view-btn">
                              <Visibility style={{ color: 'white' }} />
                            </IconButton>
                          </Tooltip>
                          {canEditTransaction && (
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleOpenModal('edit', item.Id)} disabled={item.IsConverted} className="edit-btn">
                                <Edit style={{ color: 'white' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {/* Show Create Sales Order button for verified, unconverted quotations */}
                          {item.IsVerified && !item.IsConverted && (
                            <Tooltip title="Create Sales Order">
                              <IconButton
                                size="small"
                                onClick={() => handleConvertToSalesOrder(item.Id, item.QuotationNo)}
                                className="convert-btn"
                                sx={{
                                  backgroundColor: '#7c3aed',
                                  '&:hover': { backgroundColor: '#6d28d9' },
                                  ml: 0.5
                                }}
                              >
                                <ShoppingCart style={{ color: 'white', fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canDeleteTransaction && (
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => handleOpenDeleteModal(item.Id, item.QuotationNo)} disabled={item.IsConverted} className="delete-btn">
                                <Delete style={{ color: 'white' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                      {loading ? 'Loading quotations...' :
                        Array.isArray(quotations) ?
                          (quotations.length === 0 ? 'No quotations found' : 'No quotations match current filters') :
                          'Error loading quotations - invalid data format'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination-section">
            <div className="pagination-info">
              <span>Showing {totalCount > 0 ? startIndex + 1 : 0} to {Math.min(startIndex + rowsPerPage, totalCount)} of {totalCount} entries</span>
              <div className="rows-per-page">
                <span>Rows per page:</span>
                <select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
            <div className="pagination-controls">
              <button className="pagination-btn" onClick={() => handlePageChange(1)} disabled={currentPage === 1}>First</button>
              <button className="pagination-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>Prev</button>
              {[...Array(totalPages)].map((_, i) => {
                const page = i + 1;
                if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                  return (
                    <button key={page} className={`pagination-btn ${currentPage === page ? 'active' : ''}`} onClick={() => handlePageChange(page)}>
                      {page}
                    </button>
                  );
                } else if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} className="pagination-ellipsis">...</span>;
                }
                return null;
              })}
              <button className="pagination-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0}>Next</button>
              <button className="pagination-btn" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages || totalPages === 0}>Last</button>
            </div>
          </div>
        </div>
      </div>

      <LoadingOverlay open={loading} message={loadingMessage} />

      <QuotationModal
        open={openModal}
        onClose={handleCloseModal}
        modalAction={modalAction}
        selectedItem={selectedItem}
        formData={formData}
        setFormData={setFormData}
        quotationDetails={quotationDetails}
        setQuotationDetails={setQuotationDetails}
        onSave={handleSave}
        onVerify={handleVerifyQuotation}
        customers={customers}
        items={items}
        loading={loading}
        freightPackingGST={freightPackingGST}
      />

      <SaleOrderModal
        open={salesOrderModalOpen}
        onClose={handleCloseSalesOrderModal}
        modalAction="create"
        formData={salesOrderFormData}
        setFormData={setSalesOrderFormData}
        SaleOrderDetails={salesOrderDetails}
        setSaleOrderDetails={setSalesOrderDetails}
        onSave={handleSaveSalesOrder}
        customers={customers}
        items={items}
        loading={loading}
        freightPackingGST={freightPackingGST}
      />

      <Snackbar open={!!success} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity="success">{success}</Alert>
      </Snackbar>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity="error">{error}</Alert>
      </Snackbar>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ backgroundColor: '#fef2f2', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Warning sx={{ color: '#dc2626', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>Delete Quotation</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            Are you sure you want to delete quotation <strong style={{ color: '#1e293b' }}>{deleteItemNo}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ borderColor: '#e2e8f0', color: '#64748b', '&:hover': { borderColor: '#cbd5e1', backgroundColor: '#f8fafc' } }}>
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" sx={{ backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Loading Spinner */}
      {modalLoading && <LoadingOverlay message="Loading..." />}
    </div>
  );
};

export default QuotationIndex;